import type { EmployeeDirectorySnapshot } from '../../people/public';
import type { WorkflowApprovalRoleOption } from '../approval-settings/public';
import type { WorkflowRequest } from '../model';

type SnapshotStep = NonNullable<WorkflowRequest['approvalSteps']>[number];
export type ApprovalProgressState = 'completed' | 'current' | 'pending' | 'denied' | 'returned' | 'cancelled' | 'skipped' | 'stopped' | 'unknown';
export type ApprovalProgressLookup = {
  directory?: EmployeeDirectorySnapshot;
  roleOptions?: readonly WorkflowApprovalRoleOption[];
  directoryLoading?: boolean;
  directoryError?: string;
  rolesLoading?: boolean;
  rolesError?: string;
};

export type ApprovalProgressStep = {
  order: number;
  label: string;
  assigneeName: string;
  assigneeDetail?: string;
  profilePhotoUrl?: string;
  assigneeType: SnapshotStep['assigneeType'];
  resolved: boolean;
  state: ApprovalProgressState;
};

export type ApprovalProgressStage = {
  order: number;
  state: ApprovalProgressState;
  steps: ApprovalProgressStep[];
};

export const approvalProgressStateLabels: Record<ApprovalProgressState, string> = {
  completed: '완료', current: '승인 대기', pending: '예정', denied: '반려', cancelled: '취소',
  skipped: '생략', stopped: '진행 종료', unknown: '상태 확인 필요', returned: '보완 요청',
};

function stepState(status: string, requestStatus: WorkflowRequest['status']): ApprovalProgressState {
  if (status === 'COMPLETED') return 'completed';
  if (status === 'DENIED') return 'denied';
  if (status === 'RETURNED') return 'returned';
  if (status === 'CANCELLED') return 'cancelled';
  if (status === 'SKIPPED') return 'skipped';
  if (status === 'IN_PROGRESS' || status === 'PENDING') {
    // 종료된 요청에 남은 대기 단계를 새 승인 대기로 안내하지 않습니다.
    if (requestStatus !== 'IN_PROGRESS') return 'stopped';
    return status === 'IN_PROGRESS' ? 'current' : 'pending';
  }
  return 'unknown';
}

function resolveAssignee(step: SnapshotStep, lookup: ApprovalProgressLookup): Pick<ApprovalProgressStep,
  'assigneeName' | 'assigneeDetail' | 'profilePhotoUrl' | 'resolved'> {
  // 제출 시 확정된 assignee만 사용합니다. resolution의 조직장·필드 resolver는 다시 실행하지 않습니다.
  if (step.assigneeType === 'ROLE') {
    // ROLE의 공개 assigneeId와 역할 검색의 roleCode는 서로 다른 식별자입니다.
    // 제출 시 함께 저장한 roleCode는 이름 조회에만 사용하며 승인 대상을 다시 계산하지 않습니다.
    const storedRoleCode = typeof step.resolution?.roleCode === 'string' ? step.resolution.roleCode : undefined;
    const role = lookup.roleOptions?.find((candidate) => candidate.roleCode === (storedRoleCode || step.assigneeId));
    if (role?.descriptor.trim()) return { assigneeName: role.descriptor, assigneeDetail: '승인 역할', resolved: true };
    return {
      assigneeName: '지정된 승인 역할', resolved: false,
      assigneeDetail: lookup.rolesLoading ? '이름 확인 중' : lookup.rolesError ? '역할 정보를 불러오지 못했습니다.' : '현재 조회 목록에서 역할 이름을 확인할 수 없습니다.',
    };
  }
  const member = lookup.directory?.members.find((candidate) => candidate.personId === step.assigneeId);
  if (member?.name.trim()) {
    return {
      assigneeName: member.name,
      assigneeDetail: member.title?.trim() || undefined,
      profilePhotoUrl: member.profilePhotoUrl,
      resolved: true,
    };
  }
  return {
    assigneeName: '지정된 승인자', resolved: false,
    assigneeDetail: lookup.directoryLoading ? '이름 확인 중' : lookup.directoryError ? '구성원 정보를 불러오지 못했습니다.' : '현재 조회 목록에서 구성원 이름을 확인할 수 없습니다.',
  };
}

function stageState(steps: ApprovalProgressStep[]): ApprovalProgressState {
  for (const state of ['denied', 'returned', 'current', 'unknown', 'stopped', 'pending', 'cancelled'] as const) {
    if (steps.some((step) => step.state === state)) return state;
  }
  return steps.every((step) => step.state === 'skipped') ? 'skipped' : 'completed';
}

export function workflowApprovalProgress(request: WorkflowRequest, lookup: ApprovalProgressLookup = {}) {
  const groups = new Map<number, ApprovalProgressStage>();
  for (const step of [...(request.approvalSteps ?? [])].sort((left, right) => left.order - right.order)) {
    const order = step.stageOrder || step.order;
    const group = groups.get(order) ?? { order, state: 'unknown' as const, steps: [] };
    group.steps.push({
      order: step.order,
      label: step.label,
      assigneeType: step.assigneeType,
      ...resolveAssignee(step, lookup),
      state: stepState(step.status, request.status),
    });
    groups.set(order, group);
  }
  const stages = [...groups.values()].sort((left, right) => left.order - right.order);
  stages.forEach((stage) => { stage.state = stageState(stage.steps); });
  const currentStages = request.status === 'IN_PROGRESS' ? stages.filter((stage) => stage.state === 'current') : [];
  const currentSteps = currentStages.flatMap((stage) => stage.steps.filter((step) => step.state === 'current'));
  const waitingNames = currentSteps.slice(0, 2).map((step) => step.assigneeName).join(' · ');
  const waitingSummary = waitingNames + (currentSteps.length > 2 ? ` 외 ${currentSteps.length - 2}명` : '');
  let summary: string;
  let detail: string;
  let state: ApprovalProgressState;
  switch (request.status) {
    case 'DRAFT':
      summary = '작성 중'; detail = '제출하면 결재 진행 상황을 확인할 수 있습니다.'; state = 'pending'; break;
    case 'COMPLETED':
      summary = '결재 완료'; detail = '모든 결재 절차가 끝났습니다.'; state = 'completed'; break;
    case 'DENIED':
      summary = '반려된 요청'; detail = '결재가 반려되어 진행이 종료되었습니다.'; state = 'denied'; break;
    case 'NEEDS_CHANGES':
      summary = '요청자 보완 대기'; detail = '수정 후 재제출하면 첫 단계부터 다시 결재합니다.'; state = 'returned'; break;
    case 'CANCELLED':
      summary = '취소된 요청'; detail = '요청이 취소되어 진행이 종료되었습니다.'; state = 'cancelled'; break;
    default:
      summary = currentStages.length === 1 ? `${currentStages[0].order}단계 승인 대기` : currentStages.length > 1 ? `${currentStages.length}개 단계 진행 중` : '결재 진행 중';
      detail = waitingSummary || (stages.length ? '현재 승인 대기자를 확인할 수 없습니다.' : '저장된 결재 단계가 없습니다.');
      state = currentSteps.length ? 'current' : 'unknown';
  }
  return {
    summary, detail, state, stages,
    completedStageCount: stages.filter((stage) => stage.state === 'completed').length,
    skippedStageCount: stages.filter((stage) => stage.state === 'skipped').length,
  };
}
